import { Auth } from 'aws-amplify';
import { agaveService } from 'app/services/agave.service';
import { reportCdkRefusal } from 'app/services/session/cdkRefusalSignal';
import {
  getQbwcConnection as getQbwcConnectionQuery,
  qbwcConnectionCreate as qbwcConnectionCreateMutation,
  qbwcConnectionDelete as qbwcConnectionDeleteMutation,
  qbwcConnectionSetEnabled as qbwcConnectionSetEnabledMutation,
} from 'graphql_custom/qbwc-connection';

const CDK_APPSYNC_ENDPOINT = process.env.REACT_APP_CDK_APPSYNC_ENDPOINT || '';

const qboAuthUrlCreateMutation = `
  mutation QboAuthUrlCreate($paidolId: ID!) {
    qboAuthUrlCreate(paidolId: $paidolId) {
      authUrl
    }
  }
`;

const intacctAuthUrlCreateMutation = `
  mutation IntacctAuthUrlCreate($paidolId: ID!, $redirectOrigin: String) {
    intacctAuthUrlCreate(paidolId: $paidolId, redirectOrigin: $redirectOrigin) {
      authUrl
    }
  }
`;

// SEE-1175 — Acumatica per-tenant OAuth connect. The customer supplies their
// instance URL + Client ID + Client Secret (registered in their own Acumatica
// instance); the resolver stashes them on the OAuth state record and returns the
// instance's authorize URL for the redirect.
const acumaticaConnectionInitiateMutation = `
  mutation AcumaticaConnectionInitiate($paidolId: ID!, $instanceUrl: String!, $clientId: String!, $clientSecret: String!, $redirectOrigin: String, $endpointName: String, $apiVersion: String) {
    acumaticaConnectionInitiate(paidolId: $paidolId, instanceUrl: $instanceUrl, clientId: $clientId, clientSecret: $clientSecret, redirectOrigin: $redirectOrigin, endpointName: $endpointName, apiVersion: $apiVersion) {
      authUrl
    }
  }
`;

const erpDisconnectMutation = `
  mutation ErpDisconnect($paidolId: ID!, $connectionId: ID!) {
    erpDisconnect(paidolId: $paidolId, connectionId: $connectionId) {
      success
    }
  }
`;

const spectrumConnectionCreateMutation = `
  mutation SpectrumConnectionCreate($paidolId: ID!) {
    spectrumConnectionCreate(paidolId: $paidolId) {
      connectionId
      apiKey
      endpointUrl
    }
  }
`;

const vistaConnectionCreateMutation = `
  mutation VistaConnectionCreate($paidolId: ID!) {
    vistaConnectionCreate(paidolId: $paidolId) {
      connectionId
      apiKey
      endpointUrl
    }
  }
`;

export interface SpectrumConnectionResult {
  connectionId: string;
  apiKey: string;
  endpointUrl: string;
}

export interface VistaConnectionResult {
  connectionId: string;
  apiKey: string;
  endpointUrl: string;
}

export interface AcumaticaConnectionInitiateResult {
  authUrl: string;
}

export interface IngestEntityResult {
  status: 'success' | 'partial' | 'failed';
  created: number;
  updated: number;
  errors: number;
  error?: string;
  at: string;
}

export interface ERPConnectionInfo {
  connectionId: string;
  erpType: string;
  connectionStatus: string;
  paidolId: string;
  lastSyncAt?: string;
  lastIngestStatus?: 'success' | 'partial' | 'failed';
  lastIngestResults?: string; // JSON string — parse with parseIngestResults()
  createdAt?: string;
}

const getErpConnectionQuery = `
  query GetErpConnection($connectionId: ID!) {
    getErpConnection(connectionId: $connectionId) {
      connectionId
      erpType
      connectionStatus
      paidolId
      lastSyncAt
      lastIngestStatus
      lastIngestResults
      createdAt
    }
  }
`;

// SEE-1062 — connect-time entity picker. A top-level Sage user lands on the
// connection page with ?intacct_auth=needs_entity_selection; we list the
// company's entities and persist the chosen one (scoping sync/posting/coding).
const intacctEntitiesListQuery = `
  query IntacctEntitiesList($paidolId: ID!, $connectionId: ID!) {
    intacctEntitiesList(paidolId: $paidolId, connectionId: $connectionId) {
      id
      name
    }
  }
`;

const intacctEntitySelectMutation = `
  mutation IntacctEntitySelect(
    $paidolId: ID!
    $connectionId: ID!
    $entityId: String
    $topLevel: Boolean
  ) {
    intacctEntitySelect(
      paidolId: $paidolId
      connectionId: $connectionId
      entityId: $entityId
      topLevel: $topLevel
    ) {
      connectionId
      selectedEntityId
      selectedEntityName
    }
  }
`;

export interface IntacctEntity {
  id: string;
  name: string;
}

export interface IntacctEntitySelectResult {
  connectionId: string;
  selectedEntityId: string | null;
  selectedEntityName: string | null;
}

async function cdkGraphqlRequest<T>(query: string, variables: Record<string, unknown>): Promise<T> {
  if (!CDK_APPSYNC_ENDPOINT) {
    throw new Error('CDK AppSync endpoint is not configured');
  }

  const session = await Auth.currentSession();
  const jwt = session.getIdToken().getJwtToken();

  const response = await fetch(CDK_APPSYNC_ENDPOINT, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: jwt,
    },
    body: JSON.stringify({ query, variables }),
  });

  const json = await response.json();
  reportCdkRefusal(json?.errors, { authMode: 'cognito' });

  if (json.errors?.length) {
    throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
  }

  return json.data;
}

export async function getQboAuthUrl(paidolId: string): Promise<string> {
  const data = await cdkGraphqlRequest<{ qboAuthUrlCreate: { authUrl: string } }>(qboAuthUrlCreateMutation, {
    paidolId,
  });
  return data.qboAuthUrlCreate.authUrl;
}

/**
 * @param redirectOrigin "onboarding" routes the post-OAuth callback back to the
 * onboarding stepper (so the Sage entity picker surfaces there); omitted/other
 * defaults to the settings connectivity page (SEE-1062).
 */
export async function getIntacctAuthUrl(paidolId: string, redirectOrigin?: string): Promise<string> {
  const data = await cdkGraphqlRequest<{ intacctAuthUrlCreate: { authUrl: string } }>(
    intacctAuthUrlCreateMutation,
    { paidolId, redirectOrigin: redirectOrigin ?? null }
  );
  return data.intacctAuthUrlCreate.authUrl;
}

/**
 * SEE-1175 — Begin an Acumatica direct connection. Unlike QBO/Intacct (one
 * central Speedchain client), Acumatica is per-tenant, so the customer supplies
 * their own instance URL + Client ID + Client Secret. Returns the instance's
 * authorize URL; the caller redirects the browser there to complete OAuth.
 */
export async function acumaticaConnectionInitiate(
  paidolId: string,
  instanceUrl: string,
  clientId: string,
  clientSecret: string,
  redirectOrigin?: string,
  endpointName?: string,
  apiVersion?: string
): Promise<AcumaticaConnectionInitiateResult> {
  const data = await cdkGraphqlRequest<{
    acumaticaConnectionInitiate: AcumaticaConnectionInitiateResult;
  }>(acumaticaConnectionInitiateMutation, {
    paidolId,
    instanceUrl,
    clientId,
    clientSecret,
    redirectOrigin: redirectOrigin ?? null,
    // Optional extended web-service endpoint (SM207060); null = stock Default.
    endpointName: endpointName?.trim() ? endpointName.trim() : null,
    apiVersion: apiVersion?.trim() ? apiVersion.trim() : null,
  });
  return data.acumaticaConnectionInitiate;
}

export async function disconnectErp(paidolId: string, connectionId: string): Promise<boolean> {
  const data = await cdkGraphqlRequest<{ erpDisconnect: { success: boolean } }>(erpDisconnectMutation, {
    paidolId,
    connectionId,
  });
  return data.erpDisconnect.success;
}

// SEE-1030 — flip an Agave paidol to a direct connection. Pass the prior QBO
// realmId/name (captured from getAgaveAccount before the token is cleared) so
// the cutover reconciliation can confirm the reconnect targets the same company.
const paidolSwapToDirectMutation = `
  mutation PaidolSwapToDirect($paidolId: ID!, $priorErpRealmId: String, $priorErpName: String) {
    paidolSwapToDirect(paidolId: $paidolId, priorErpRealmId: $priorErpRealmId, priorErpName: $priorErpName) {
      success
    }
  }
`;

export async function swapToDirect(
  paidolId: string,
  priorErpRealmId?: string,
  priorErpName?: string
): Promise<boolean> {
  const data = await cdkGraphqlRequest<{ paidolSwapToDirect: { success: boolean } }>(
    paidolSwapToDirectMutation,
    { paidolId, priorErpRealmId, priorErpName }
  );
  return data.paidolSwapToDirect.success;
}

const triggerErpSyncMutation = `
  mutation TriggerErpSync($paidolId: ID!) {
    triggerErpSync(paidolId: $paidolId) {
      success
      message
      taskArn
    }
  }
`;

export interface ErpSyncResponse {
  success: boolean;
  message: string;
  taskArn?: string;
}

export async function triggerErpSync(paidolId: string): Promise<ErpSyncResponse> {
  const data = await cdkGraphqlRequest<{ triggerErpSync: ErpSyncResponse }>(triggerErpSyncMutation, {
    paidolId,
  });
  return data.triggerErpSync;
}

export async function createSpectrumConnection(paidolId: string): Promise<SpectrumConnectionResult> {
  const data = await cdkGraphqlRequest<{ spectrumConnectionCreate: SpectrumConnectionResult }>(
    spectrumConnectionCreateMutation,
    { paidolId }
  );
  return data.spectrumConnectionCreate;
}

export async function createVistaConnection(paidolId: string): Promise<VistaConnectionResult> {
  const data = await cdkGraphqlRequest<{ vistaConnectionCreate: VistaConnectionResult }>(
    vistaConnectionCreateMutation,
    { paidolId }
  );
  return data.vistaConnectionCreate;
}

// SEE-1050 — Sage 300 CRE via hh2: credential-entry connect (no OAuth).
// The resolver validates the site credentials against hh2 before saving, so a
// thrown error here carries hh2's specific rejection (invalid creds / locked
// account / unreachable site) for display.
const sage300ConnectionCreateMutation = `
  mutation Sage300ConnectionCreate($paidolId: ID!, $site: String!, $username: String!, $password: String!) {
    sage300ConnectionCreate(paidolId: $paidolId, site: $site, username: $username, password: $password) {
      connectionId
      connectionStatus
    }
  }
`;

export interface Sage300ConnectionResult {
  connectionId: string;
  connectionStatus: string;
}

export async function createSage300Connection(
  paidolId: string,
  site: string,
  username: string,
  password: string
): Promise<Sage300ConnectionResult> {
  const data = await cdkGraphqlRequest<{ sage300ConnectionCreate: Sage300ConnectionResult }>(
    sage300ConnectionCreateMutation,
    { paidolId, site, username, password }
  );
  return data.sage300ConnectionCreate;
}

export async function getErpConnection(connectionId: string): Promise<ERPConnectionInfo | null> {
  const data = await cdkGraphqlRequest<{ getErpConnection: ERPConnectionInfo | null }>(
    getErpConnectionQuery,
    { connectionId }
  );
  return data.getErpConnection;
}

export async function listIntacctEntities(paidolId: string, connectionId: string): Promise<IntacctEntity[]> {
  const data = await cdkGraphqlRequest<{ intacctEntitiesList: IntacctEntity[] | null }>(
    intacctEntitiesListQuery,
    { paidolId, connectionId }
  );
  return data.intacctEntitiesList ?? [];
}

// entityId is omitted (null) for the company-wide activation path — a
// single-entity Intacct company with no selectable entities.
// SEE-1261: topLevel=true is an explicit company-wide activation on a
// MULTI-entity company (all entities' codes sync into this paidol). It is
// distinct from an omitted entityId so a missing selection can't skip scoping.
export async function selectIntacctEntity(
  paidolId: string,
  connectionId: string,
  entityId?: string,
  topLevel?: boolean
): Promise<IntacctEntitySelectResult> {
  const data = await cdkGraphqlRequest<{ intacctEntitySelect: IntacctEntitySelectResult }>(
    intacctEntitySelectMutation,
    { paidolId, connectionId, entityId: entityId ?? null, topLevel: topLevel ?? null }
  );
  return data.intacctEntitySelect;
}

// SEE-683 — direct-ERP posting via the CDK erp-conn-post Lambda.
const startErpPostingMutation = `
  mutation StartErpPosting($input: StartErpPostingInput!) {
    startErpPosting(input: $input) {
      transactionId
      status
      externalId
      error
      warning
    }
  }
`;

export type ErpPostingStatus = 'POSTED' | 'FAILED' | 'PENDING';

export interface ErpPostingResult {
  transactionId: string;
  status: ErpPostingStatus;
  externalId?: string | null;
  error?: string | null;
  /** SEE-1696: set on POSTED when the post degraded (Sage Intacct billable fallback). */
  warning?: string | null;
}

export async function startErpPosting(input: {
  paidolId: string;
  transactionId: string;
}): Promise<ErpPostingResult | null> {
  const data = await cdkGraphqlRequest<{ startErpPosting: ErpPostingResult | null }>(
    startErpPostingMutation,
    { input }
  );
  return data.startErpPosting;
}

// SEE-835 — posting-account selection (Connections page). Lets a customer choose
// which ERP record (Sage Intacct CREDITCARD / QBO account) their transactions
// post against, instead of defaulting to the record named "Speedchain".
export interface ErpPostingAccount {
  accountId: string;
  accountName?: string | null;
}

const getErpPostingAccountQuery = `
  query GetErpPostingAccount($paidolId: ID!, $erp: String!) {
    getErpPostingAccount(paidolId: $paidolId, erp: $erp) {
      accountId
      accountName
    }
  }
`;

const setErpPostingAccountMutation = `
  mutation SetErpPostingAccount($paidolId: ID!, $erp: String!, $accountId: String, $accountName: String) {
    setErpPostingAccount(paidolId: $paidolId, erp: $erp, accountId: $accountId, accountName: $accountName) {
      accountId
      accountName
    }
  }
`;

const getSageIntacctCreditCardsQuery = `
  query GetSageIntacctCreditCards($input: GetSageIntacctCreditCardsInput!) {
    getSageIntacctCreditCards(input: $input) {
      accountId
      accountName
    }
  }
`;

const getAgaveBankAccountsQuery = `
  query GetAgaveBankAccounts($input: AgaveBankAccountsInput!) {
    getAgaveBankAccounts(input: $input) {
      id
      name
    }
  }
`;

// SEE-835 — posting-account selection is a direct-connect-only feature, scoped to
// the ERPs that have both a direct connection and a posting backend: Sage Intacct
// (CREDITCARD) and QBO (bank/GL account). Acumatica posts against the
// per-transaction merchant vendor, so there is no single posting account; QBD has
// no direct connection yet.
// SEE-1862 — Sage 300 CRE joins: its Direct Costs credit a GL account, and a Sage
// company shared by several entities has one "Speedchain" account per entity, so
// each company picks its own.
export const ERPS_WITH_POSTING_ACCOUNT = ['sage-intacct', 'quick-books-online', 'sage-300-cre'];

// SEE-1862 — the Sage 300 picker lists the synced GL accounts matching this name
// (the ERP setup step creates it), labelled with the account number because the
// entities' accounts share the name.
const SAGE300_POSTING_ACCOUNT_SEARCH = 'Speedchain';

// SEE-835 — vendor-model direct ERPs (AppXchange). The posting target is the AP
// invoice header VENDOR, not a credit-card/bank account. Same selection UI, but
// the dropdown lists synced vendors and the copy says "vendor".
export const ERPS_WITH_POSTING_VENDOR = ['vista-hosted', 'spectrum'];

export function erpSupportsPostingAccount(erp?: string | null): boolean {
  return !!erp && (ERPS_WITH_POSTING_ACCOUNT.includes(erp) || ERPS_WITH_POSTING_VENDOR.includes(erp));
}

// 'vendor' for AppXchange vendor-model ERPs, 'account' otherwise. Drives the
// posting-section copy and which list query runs.
export function erpPostingTargetKind(erp?: string | null): 'account' | 'vendor' {
  return erp && ERPS_WITH_POSTING_VENDOR.includes(erp) ? 'vendor' : 'account';
}

// SEE-835: the Spectrum vendor lock reads getErpPostingAccount once per transaction
// table cell, so a single page would otherwise fire N identical requests on mount.
// Dedupe + cache the in-flight/resolved read per (paidolId, erp); setErpPostingAccount
// invalidates the affected key so the next read is fresh after a selection change.
const postingAccountCache = new Map<string, Promise<ErpPostingAccount | null>>();

const postingAccountCacheKey = (paidolId: string, erp: string) => `${paidolId}:${erp}`;

export function getErpPostingAccount(paidolId: string, erp: string): Promise<ErpPostingAccount | null> {
  const key = postingAccountCacheKey(paidolId, erp);
  const cached = postingAccountCache.get(key);
  if (cached) return cached;

  const request = cdkGraphqlRequest<{ getErpPostingAccount: ErpPostingAccount | null }>(
    getErpPostingAccountQuery,
    {
      paidolId,
      erp,
    }
  ).then((data) => data.getErpPostingAccount);
  postingAccountCache.set(key, request);
  // Evict on failure so a transient error doesn't poison the cache for later reads.
  request.catch(() => {
    if (postingAccountCache.get(key) === request) postingAccountCache.delete(key);
  });
  return request;
}

export async function setErpPostingAccount(
  paidolId: string,
  erp: string,
  accountId: string | null,
  accountName?: string | null
): Promise<ErpPostingAccount | null> {
  const data = await cdkGraphqlRequest<{ setErpPostingAccount: ErpPostingAccount | null }>(
    setErpPostingAccountMutation,
    { paidolId, erp, accountId, accountName }
  );
  // The selection changed — drop the cached read so the next fetch reflects it.
  postingAccountCache.delete(postingAccountCacheKey(paidolId, erp));
  return data.setErpPostingAccount;
}

// SEE-835 — direct Sage Intacct CREDITCARD list. No Agave token: reads over the
// connection's stored OAuth session, scoped to the paidol's selected entity.
// Backed by the listSageIntacctCreditCards CDK resolver.
const listSageIntacctCreditCardsQuery = `
  query ListSageIntacctCreditCards($paidolId: ID!, $connectionId: ID!) {
    listSageIntacctCreditCards(paidolId: $paidolId, connectionId: $connectionId) {
      accountId
      accountName
    }
  }
`;

// SEE-835 — direct QuickBooks Online bank/credit-card account list. No Agave
// token: reads over the connection's OAuth session. Backed by the
// listQboBankAccounts CDK resolver.
const listQboBankAccountsQuery = `
  query ListQboBankAccounts($paidolId: ID!, $connectionId: ID!) {
    listQboBankAccounts(paidolId: $paidolId, connectionId: $connectionId) {
      accountId
      accountName
    }
  }
`;

// SEE-835 — synced ERP vendor list for vendor-model direct ERPs (Vista, Spectrum).
// connectionId is accepted but unused server-side (vendors are per-paidol, read
// from the synced AgaveVendor table). Backed by the listErpVendors CDK resolver.
const listErpVendorsQuery = `
  query ListErpVendors($paidolId: ID!, $connectionId: ID) {
    listErpVendors(paidolId: $paidolId, connectionId: $connectionId) {
      accountId
      accountName
    }
  }
`;

export interface ListPostingAccountsOpts {
  paidolId: string;
  /** Direct ERPConnection id — present for direct connections (the only ones
   *  that show the posting-account section). */
  connectionId?: string | null;
  /** Legacy Agave account token — fallback for any pre-migration Agave path. */
  accountToken?: string | null;
}

// Lists selectable posting accounts for a (direct) ERP connection.
export async function listErpPostingAccounts(
  erp: string,
  opts: ListPostingAccountsOpts
): Promise<ErpPostingAccount[]> {
  const { paidolId, connectionId, accountToken } = opts;
  if (erp === 'sage-intacct') {
    // Direct connection: list CREDITCARD records over the connection's OAuth
    // session (scoped to the selected entity) — no Agave token needed.
    if (connectionId) {
      const data = await cdkGraphqlRequest<{ listSageIntacctCreditCards: ErpPostingAccount[] | null }>(
        listSageIntacctCreditCardsQuery,
        { paidolId, connectionId }
      );
      return data.listSageIntacctCreditCards ?? [];
    }
    // Legacy Agave passthrough (pre-migration Intacct).
    if (accountToken) {
      const data = await cdkGraphqlRequest<{ getSageIntacctCreditCards: ErpPostingAccount[] | null }>(
        getSageIntacctCreditCardsQuery,
        { input: { account_token: accountToken } }
      );
      return data.getSageIntacctCreditCards ?? [];
    }
    return [];
  }
  if (erp === 'quick-books-online') {
    // Direct connection: list bank / credit-card accounts over the connection's
    // OAuth session — no Agave token needed.
    if (connectionId) {
      const data = await cdkGraphqlRequest<{ listQboBankAccounts: ErpPostingAccount[] | null }>(
        listQboBankAccountsQuery,
        { paidolId, connectionId }
      );
      return data.listQboBankAccounts ?? [];
    }
    // Legacy Agave passthrough (pre-migration QBO).
    if (accountToken) {
      const data = await cdkGraphqlRequest<{
        getAgaveBankAccounts: { id: string; name: string }[] | null;
      }>(getAgaveBankAccountsQuery, { input: { account_token: accountToken } });
      return (data.getAgaveBankAccounts ?? []).map((a) => ({ accountId: a.id, accountName: a.name }));
    }
    return [];
  }
  if (erp === 'sage-300-cre') {
    // SEE-1862: synced GL accounts named like "Speedchain". The stored id is the
    // AgaveGlCode id; the poster resolves it to its account number.
    const result = await agaveService.v2SearchAgaveGLCodes({
      input: {
        filter: { paidolId, searchTerm: SAGE300_POSTING_ACCOUNT_SEARCH, isSettingsRequest: true },
        limit: 100,
      },
    });
    return (result?.items ?? [])
      .filter((gl): gl is NonNullable<typeof gl> => !!gl?.id)
      .map((gl) => ({
        accountId: gl.id,
        accountName: gl.source_id ? `${gl.name ?? gl.source_id} (${gl.source_id})` : gl.name ?? gl.id,
      }));
  }
  if (erp === 'vista-hosted' || erp === 'spectrum') {
    // Vendor-model direct ERPs: list the paidol's synced vendors for the
    // posting-vendor dropdown (no Agave token; read from AgaveVendor by paidolId).
    const data = await cdkGraphqlRequest<{ listErpVendors: ErpPostingAccount[] | null }>(
      listErpVendorsQuery,
      { paidolId, connectionId }
    );
    return data.listErpVendors ?? [];
  }
  return [];
}

// SEE-1042 — QuickBooks Desktop (QBD) direct connect flow. Calls the CDK
// qbwc-connection API (SEE-1039) over the same cdkGraphqlRequest (JWT) path the
// QBO direct flow uses. QBD connections are tracked in the QbwcConnection table
// keyed by paidolId — separate from the ERPConnection table used by getErpConnection.

// Returned exactly once by qbwcConnectionCreate. oneTimePassword is reveal-once:
// never logged, persisted, or sent to analytics. qwcFileContent is the .qwc XML
// (the frontend turns it into a client-side download — there is no URL).
export interface QbwcCreateResult {
  connectionId: string;
  qbwcUsername: string;
  oneTimePassword: string;
  qwcFileContent: string;
}

export interface QbwcSyncResult {
  name: string;
  ok: boolean;
  detail?: string | null;
}

export interface QbwcConnectionStatus {
  connectionId: string;
  enabled: boolean;
  // ISO-8601, null until the first poll closes.
  lastSyncAt?: string | null;
  // SEE-1042 — real Started/Ended of the last sync run (session open -> close),
  // recorded host-side. Null on connections last synced before this shipped.
  syncStartedAt?: string | null;
  syncEndedAt?: string | null;
  // true once a sync completed without errors.
  lastSyncOk?: boolean | null;
  // e.g. "C:\QB\Sample.qbw", filled on first AccountQuery.
  companyFile?: string | null;
  lastResults?: QbwcSyncResult[] | null;
}

export async function createQbwcConnection(paidolId: string): Promise<QbwcCreateResult> {
  const data = await cdkGraphqlRequest<{ qbwcConnectionCreate: QbwcCreateResult }>(
    qbwcConnectionCreateMutation,
    { paidolId }
  );
  return data.qbwcConnectionCreate;
}

// Returns null when no connection exists yet (or before the first poll closes) —
// this is the expected pre-create / pre-first-sync state, not an error.
export async function fetchQbwcConnection(paidolId: string): Promise<QbwcConnectionStatus | null> {
  const data = await cdkGraphqlRequest<{ getQbwcConnection: QbwcConnectionStatus | null }>(
    getQbwcConnectionQuery,
    { paidolId }
  );
  return data.getQbwcConnection;
}

// Pauses/resumes the connection without deleting it (the host gates polling on
// `enabled`). Backs the pause/resume affordance; a full removal is the separate
// deleteQbwcConnection below.
export async function setQbwcConnectionEnabled(
  paidolId: string,
  enabled: boolean
): Promise<{ connectionId: string; enabled: boolean }> {
  const data = await cdkGraphqlRequest<{
    qbwcConnectionSetEnabled: { connectionId: string; enabled: boolean };
  }>(qbwcConnectionSetEnabledMutation, { paidolId, enabled });
  return data.qbwcConnectionSetEnabled;
}

// Disconnect: removes the connection entirely (registry + credential). `deleted`
// is false when no connection existed (no-op). After this, fetchQbwcConnection
// returns null and the page returns to the Connect state.
export async function deleteQbwcConnection(
  paidolId: string
): Promise<{ connectionId: string | null; deleted: boolean }> {
  const data = await cdkGraphqlRequest<{
    qbwcConnectionDelete: { connectionId: string | null; deleted: boolean };
  }>(qbwcConnectionDeleteMutation, { paidolId });
  return data.qbwcConnectionDelete;
}

// SEE-1017 — synced active Non-Inventory items for the Sage Intacct billable-item
// dropdown. Reuses the ErpPostingAccount shape (accountId = ITEMID, the value
// stored as the paidol's billable item). connectionId is accepted but unused
// server-side (items are per-paidol, read from the synced ERPItem table). Backed
// by the listErpItems CDK resolver (erp-connection).
const listErpItemsQuery = `
  query ListErpItems($paidolId: ID!, $connectionId: ID) {
    listErpItems(paidolId: $paidolId, connectionId: $connectionId) {
      accountId
      accountName
    }
  }
`;

// SEE-1135: the billable-item dropdown renders once per transaction table cell, so
// a single page would otherwise fire N identical listErpItems reads on mount. Cache
// + dedupe the in-flight/resolved read per (paidolId, connectionId) — the item
// catalog is the same for every row, so one fetch serves them all. Mirrors the
// getErpPostingAccount cache above. Evict on failure so a transient error doesn't
// poison the cache for later reads.
const erpItemsCache = new Map<string, Promise<ErpPostingAccount[]>>();

const erpItemsCacheKey = (paidolId: string, connectionId?: string | null) =>
  `${paidolId}:${connectionId ?? ''}`;

// Lists a paidol's synced active items for the billable-item picker.
export function listErpItems(paidolId: string, connectionId?: string | null): Promise<ErpPostingAccount[]> {
  const key = erpItemsCacheKey(paidolId, connectionId);
  const cached = erpItemsCache.get(key);
  if (cached) return cached;

  const request = cdkGraphqlRequest<{ listErpItems: ErpPostingAccount[] | null }>(listErpItemsQuery, {
    paidolId,
    connectionId,
  }).then((data) => data.listErpItems ?? []);
  erpItemsCache.set(key, request);
  request.catch(() => {
    if (erpItemsCache.get(key) === request) erpItemsCache.delete(key);
  });
  return request;
}
